import { Modal, Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { RefundableEnum } from '@common-types';
import { RootState, formatDateCheckInOut, renderNumber } from '@share/utils';
import { Currency } from '@components';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import {
  PeopleSvg,
  SuitecaseSvg,
  TransmitionSvg,
  AirSvg,
} from '@assets';

import { useQuoteCarModalCompare } from './useQuoteCarModalCompare';

import './style.scss';

const modalWidth = 900;

interface IProps {
  visible: boolean;
  quoteBase: string;
  onCancel: () => void;
}

export function QuoteCarModalCompare(props: IProps) {
  const { visible, quoteBase, onCancel } = props;
  const quoteStore = useSelector((state: RootState) => state.quoteStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);

  const {
    myQuotes,
    isHistorical,
    fontFamily,
    secondaryFontFamily,
    styleButton,
    handleView,
    modalOpen,
  } = useQuoteCarModalCompare({ quoteStore, loginStore, quoteBase });

  return (
    <Modal
      className={`quote-modal-compare ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      open={visible && modalOpen}
      footer={null}
      onCancel={onCancel}
      wrapClassName="quote-modal-compare__wrapper"
      width={modalWidth}
    >
      <div>
        <h4 className="quote-modal-compare__title secondary-font">
          <FormattedMessage id="results.car.card.compare.modal.title" />
        </h4>

        <div className="quote-modal-compare__hotels" style={{ flexDirection: 'column' }}>
          <div className="hotels-dates">
            {myQuotes[0]?.checkIn ? formatDateCheckInOut(myQuotes[0]?.checkIn, DATE_FORMAT_CHECK_IN_OUT) : 'NA'} - {myQuotes[0]?.checkOut ? formatDateCheckInOut(myQuotes[0]?.checkOut, DATE_FORMAT_CHECK_IN_OUT) : 'NA'}
          </div>
          <div className="hotels-dates">
            {myQuotes[0]?.occupancy}
          </div>
        </div>

        <div className="quote-modal-compare__hotels">
          {myQuotes.map((quote, index) => {
            let amenitiesObj: Record<string, string> = {};
            const amenities = quote?.amenities;

            if (amenities && quote.quoteType === 'Car') {
              if (typeof amenities === 'object') {
                amenitiesObj = JSON.parse(amenities as unknown as string);
              }
            }

            return (
              <div key={index} className={`quote-modal-compare__hotel ${index === 0 ? 'display-border' : ''} hotel_${myQuotes.length}`}>
                <div className="hotel-images-container carImageHeight">
                  <div className="hotel-image" style={{ backgroundImage: `url(${quote.image})` }}>
                  </div>
                </div>

                <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                  <p className="mb-0"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      lineClamp: 1,
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                    }}>
                    {quote.supplierLogo ? (
                      <div className="cars-content-location__vendor">
                        <img alt="Vendor logo" src={quote.supplierLogo} style={{ maxWidth: '100%', height: 'auto' }} />
                      </div>
                    ) : (
                      <span>{quote.displayName || '-'}</span>
                    )}
                  </p>
                </div>

                <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                  <p className="mb-0"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      lineClamp: 1,
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                    }}>
                    <Tooltip title={`${quote.displayName}`} placement="top"> {quote.displayName}</Tooltip>
                  </p>
                </div>

                <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                  <p className="mb-0"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      lineClamp: 1,
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                    }}>
                    <Tooltip title={`${quote.pickUpLocationDetail}`} placement="top"> {quote.pickUpLocationDetail}</Tooltip>
                  </p>
                </div>

                <div className="hotel-text-container" style={{ minHeight: '50px', height: 'auto' }}>
                  <div className="cars-card__item"><TransmitionSvg /><label className="ms-1">{amenitiesObj?.transmission}</label></div>
                  <div className="cars-card__item"><PeopleSvg /><label className="ms-1"><FormattedMessage id="cars.filter.seats" />: {amenitiesObj?.seats}</label></div>
                  <div className="cars-card__item"><SuitecaseSvg /><label className="ms-1"><FormattedMessage id="adult" values={{ count: amenitiesObj?.seats }} />: {amenitiesObj?.seats}</label></div>
                  <div className="cars-card__item"><SuitecaseSvg /><label className="ms-1"><FormattedMessage id="cars.baggage" />: {amenitiesObj?.luggage}</label></div>
                  <div className="cars-card__item"><AirSvg /><label className="ms-1"><FormattedMessage id="cars.ac" /></label></div>
                </div>

                {quote.refundability && quote.refundability !== RefundableEnum.Unknown ? (
                  <div className="hotel-text-container" style={{ height: 'auto ' }}>
                    <div className="hotel-card-wrapper__refundable">
                      {quote.refundability === RefundableEnum.Refundable && (
                        <div className="hotel-card-wrapper__refundable-ref">
                          <FormattedMessage id="refundable" />
                        </div>
                      )}
                      {quote.refundability === RefundableEnum.Nonrefundable && (
                        <div className="hotel-card-wrapper__refundable-nonref">
                          <FormattedMessage id="non.refundable" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                  {quote.publicPrice ? (<p style={{ textDecoration: 'line-through' }}><Currency currency={quote.currency} />{renderNumber(quote.publicPrice, 2)}</p>) : null}
                  <p><strong style={{ fontSize: '20px', fontWeight: 'bold' }}><Currency currency={quote.currency} />{renderNumber(quote.price, 2)}</strong></p>
                  {quote.discount > 0 ? (<p>{quote.discount} % <FormattedMessage id="results.hotel.card.compare.modal.off" /></p>) : null}
                </div>

                {!isHistorical ? (
                  <div className="hotel-view-container">
                    <div className="view-button" style={styleButton} onClick={() => handleView(quote)}>
                      <FormattedMessage id="results.hotel.card.compare.modal.view" />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
